import { useEffect } from "react";

// Portal-wide drag-to-resize for table columns (founder ask 2026-08-27:
// "everywhere, not just payments"). Mounted once in MasterLayout; works by
// event delegation, so every screen's tables get it with zero per-screen
// code. Hover the right edge of any column header until the cursor turns
// into a resize arrow, then drag. Widths persist per page and per column
// label in this browser.

const STORAGE_PREFIX = "table_col_widths:";
const EDGE = 10; // px hit zone at a header's right edge
const MIN_WIDTH = 60;

const keyForPage = () => STORAGE_PREFIX + window.location.pathname;
const labelOf = (th) =>
    (th.textContent || "").trim().slice(0, 40) || "col" + th.cellIndex;

const loadSaved = () => {
    try {
        return JSON.parse(localStorage.getItem(keyForPage())) || {};
    } catch {
        return {};
    }
};

// Fixed layout makes cell widths obey header widths (that is what lets
// truncation follow the column), but flipping a table into it redistributes
// unset columns — so snapshot every header's current width first and
// nothing visibly jumps.
const lockTableWidths = (table) => {
    if (!table || table.classList.contains("col-resized")) return;
    table.querySelectorAll("th").forEach((th) => {
        if (!th.style.width) {
            th.style.width = th.offsetWidth + "px";
        }
    });
    table.classList.add("col-resized");
};

const applySaved = () => {
    const saved = loadSaved();
    if (!Object.keys(saved).length) return;
    document.querySelectorAll("table th").forEach((th) => {
        const w = saved[labelOf(th)];
        if (w) {
            lockTableWidths(th.closest("table"));
            th.style.width = w + "px";
            th.style.minWidth = w + "px";
        }
    });
};

// Visible dividers (founder ask 2026-08-27): every column boundary shows a
// hairline in the header, and the line thickens in brand pink while the
// cursor is in its grab zone or a drag is underway. The colors are
// mid-tone alphas so the same line reads on both light and dark themes.
const RESIZER_CSS = `
table th { position: relative; }
table th:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 18%;
  bottom: 18%;
  right: 0;
  width: 1px;
  background: rgba(148, 163, 184, 0.45);
  pointer-events: none;
}
table th.col-resize-hot::after {
  width: 3px;
  background: #FA69B9;
}
/* Click-to-expand truncated cells (founder ask 2026-08-27): screens clamp
   long values with .text-truncate and a fixed inline max-width, so the
   "..." never yields even after widening the column. Clicking the cell
   text toggles the full value in place; !important is what lets the
   expansion beat the inline max-width. */
table td .text-truncate {
  cursor: pointer;
}
/* Dynamic truncation (founder ask 2026-08-27): once a table's columns
   have been resized, it switches to fixed layout and truncated text
   follows its COLUMN width instead of a hardcoded pixel clamp — dragging
   a column wider reveals more of the value, live. */
table.col-resized {
  table-layout: fixed;
}
table.col-resized td .text-truncate {
  max-width: 100% !important;
}
table td .text-truncate.cell-expanded {
  max-width: none !important;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: break-word;
}
`;

export default function GlobalColumnResizer() {
    useEffect(() => {
        let drag = null;
        let reapplyTimer = null;
        let hotTh = null;

        let styleEl = document.getElementById("global-col-resizer-css");
        if (!styleEl) {
            styleEl = document.createElement("style");
            styleEl.id = "global-col-resizer-css";
            styleEl.textContent = RESIZER_CSS;
            document.head.appendChild(styleEl);
        }

        const setHot = (th) => {
            if (hotTh === th) return;
            if (hotTh) hotTh.classList.remove("col-resize-hot");
            hotTh = th;
            if (hotTh) hotTh.classList.add("col-resize-hot");
        };

        const nearRightEdge = (th, x) => {
            const r = th.getBoundingClientRect();
            return x >= r.right - EDGE && x <= r.right + 2;
        };

        const thAt = (e) => (e.target?.closest ? e.target.closest("table th") : null);

        const onMouseMove = (e) => {
            if (drag) {
                const w = Math.max(MIN_WIDTH, drag.startWidth + (e.clientX - drag.startX));
                drag.th.style.width = w + "px";
                drag.th.style.minWidth = w + "px";
                drag.lastWidth = w;
                e.preventDefault();
                return;
            }
            const th = thAt(e);
            const hot = th && nearRightEdge(th, e.clientX);
            setHot(hot ? th : null);
            document.body.style.cursor = hot ? "col-resize" : "";
        };

        const onMouseDown = (e) => {
            const th = thAt(e);
            if (!th || !nearRightEdge(th, e.clientX)) return;
            e.preventDefault();
            lockTableWidths(th.closest("table"));
            drag = {
                th,
                startX: e.clientX,
                startWidth: th.offsetWidth,
                lastWidth: th.offsetWidth,
            };
            document.body.style.userSelect = "none";
        };

        const onMouseUp = () => {
            if (!drag) return;
            try {
                const saved = loadSaved();
                saved[labelOf(drag.th)] = drag.lastWidth;
                localStorage.setItem(keyForPage(), JSON.stringify(saved));
            } catch { }
            drag = null;
            setHot(null);
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
        };

        // React rebuilds tables on route changes and refetches, which wipes
        // inline widths; watch the DOM and re-apply the saved ones. The
        // observer only watches child additions, and applySaved only writes
        // style attributes, so it cannot feed itself.
        const observer = new MutationObserver(() => {
            clearTimeout(reapplyTimer);
            reapplyTimer = setTimeout(applySaved, 200);
        });
        observer.observe(document.body, { childList: true, subtree: true });
        applySaved();

        // Click a truncated cell to read all of it, click again to fold it
        // back. Cells living inside links or buttons keep their own click.
        const onClick = (e) => {
            const el = e.target?.closest ? e.target.closest("table td .text-truncate") : null;
            if (!el) return;
            if (el.closest("a, button, [role='button'], .dropdown")) return;
            el.classList.toggle("cell-expanded");
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("mouseup", onMouseUp);
        document.addEventListener("click", onClick);
        return () => {
            observer.disconnect();
            clearTimeout(reapplyTimer);
            setHot(null);
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("mouseup", onMouseUp);
            document.removeEventListener("click", onClick);
        };
    }, []);

    return null;
}
